/**
 * SuccessStories — /success-stories public page. Renders CMS-managed
 * customer case studies (falls back to seeded placeholders if the admin
 * hasn't added any yet). Ends with a CTA that funnels employers to
 * /employers.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Briefcase, IdCard, CheckCircle2, ArrowRight, Sparkles, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const kindTone = (k) => k === "talent"
    ? "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30"
    : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30";

const StoryCard = ({ story, idx }) => {
    const Icon = story.kind === "talent" ? IdCard : Briefcase;
    return (
        <motion.article
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.4, delay: idx * 0.05 }}
            data-testid={`success-story-${story.id}`}
            className="card-soft p-7 flex flex-col"
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider border ${kindTone(story.kind)}`}>
                    <Icon size={13} /> {story.kind === "talent" ? "Talent story" : "Employer story"}
                </div>
                {story.time_to_shortlist && (
                    <div className="text-[11px] font-medium text-muted-foreground inline-flex items-center gap-1">
                        <Sparkles size={11} className="text-amber-500" /> {story.time_to_shortlist}
                    </div>
                )}
            </div>
            <div className="mt-4 space-y-1">
                {story.role_hired && (
                    <div className="font-heading font-semibold text-lg text-foreground">
                        {story.role_hired}
                    </div>
                )}
                {story.company_type && (
                    <div className="text-xs text-muted-foreground">{story.company_type}</div>
                )}
            </div>
            {story.quote && (
                <p className="mt-4 text-sm text-foreground leading-relaxed">&ldquo;{story.quote}&rdquo;</p>
            )}
            {story.author_name && (
                <div className="mt-3 text-[11px] text-muted-foreground italic">— {story.author_name}</div>
            )}
            {story.results?.length > 0 && (
                <div className="mt-5 pt-5 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {story.results.map((r, i) => (
                        <div key={i} className="rounded-md bg-muted/40 p-3">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{r.label}</div>
                            <div className="mt-0.5 font-heading font-bold text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                                <CheckCircle2 size={13} /> {r.value}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </motion.article>
    );
};

export default function SuccessStories() {
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [usingDefaults, setUsingDefaults] = useState(false);

    useEffect(() => {
        let mounted = true;
        api.get("/content/success-stories")
            .then((r) => {
                if (!mounted) return;
                setStories(r.data?.stories || []);
                setUsingDefaults(!!r.data?.using_defaults);
            })
            .catch(() => { if (mounted) setStories([]); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, []);

    return (
        <div className="container-app py-14" data-testid="success-stories-page">
            <Helmet>
                <title>Success Stories · Simonara Talent Solutions</title>
                <meta name="description" content="Real hiring outcomes from teams that use Simonara. AI-matched, trust-verified talent — placed in days, not months." />
                <link rel="canonical" href="/success-stories" />
            </Helmet>

            <header className="max-w-3xl">
                <p className="overline">Real outcomes</p>
                <h1 className="mt-2 font-heading font-medium text-4xl sm:text-5xl tracking-tight text-foreground">
                    Success Stories
                </h1>
                <p className="mt-4 text-muted-foreground text-lg">
                    From both sides of the hire — the companies, the roles, the results. Every story below reflects the difference verified Talent Passports™ and AI matching make on a real hiring loop.
                </p>
                {usingDefaults && (
                    <div
                        className="mt-5 inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-3 py-1 text-[11px] font-medium"
                        data-testid="success-stories-placeholder-notice"
                    >
                        <Sparkles size={12} /> Placeholder stories — real customer stories rolling out shortly.
                    </div>
                )}
            </header>

            <section className="mt-10">
                {loading ? (
                    <div className="py-16 flex justify-center" data-testid="success-stories-loading">
                        <Loader2 size={20} className="animate-spin text-emerald-500" />
                    </div>
                ) : stories.length === 0 ? (
                    <div className="card-soft p-10 text-center" data-testid="success-stories-empty">
                        <Sparkles size={24} className="mx-auto text-muted-foreground" />
                        <div className="mt-2 font-heading font-semibold text-lg">Stories coming soon.</div>
                        <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
                            The first Simonara customers are being onboarded now. Their outcomes will land here as roles close.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="success-stories-grid">
                        {stories.map((s, idx) => (
                            <StoryCard key={s.id} story={s} idx={idx} />
                        ))}
                    </div>
                )}
            </section>

            {/* Bottom CTA — becomes an acquisition surface for employers.        */}
            <section className="mt-16" data-testid="success-stories-cta">
                <div className="card-soft p-10 text-center border-emerald-500/40 bg-gradient-to-br from-emerald-500/5 via-background to-sky-500/5">
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
                        <Sparkles size={12} /> Your turn
                    </div>
                    <h2 className="mt-3 font-heading font-medium text-3xl sm:text-4xl tracking-tight text-foreground">
                        Become our next success story.
                    </h2>
                    <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
                        Post your first role, unlock the marketplace of verified talent, and let AI matching do the shortlisting. Same-week hires are our baseline.
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                        <Link
                            to="/employers"
                            className="btn-green inline-flex items-center gap-2"
                            data-testid="success-stories-cta-employers"
                        >
                            Become our next success story <ArrowRight size={14} />
                        </Link>
                        <Link
                            to="/pricing"
                            className="btn-outline inline-flex items-center gap-2"
                            data-testid="success-stories-cta-pricing"
                        >
                            See pricing
                        </Link>
                    </div>
                </div>
            </section>

            {/* Share your story — public submission form */}
            <ShareStoryForm />
        </div>
    );
}

function ShareStoryForm() {
    const [params] = useSearchParams();
    const sectionRef = useRef(null);
    const [form, setForm] = useState({
        company_name: params.get("company_name") || "",
        role_hired: params.get("role_hired") || "",
        quote: "",
        author_name: params.get("author_name") || "",
        author_title: "",
        author_email: "",
        time_to_shortlist: "",
        permission_to_publish: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const prefilled = !!(params.get("company_name") || params.get("role_hired") || params.get("author_name"));
    const source = params.get("src") || "";

    // Auto-scroll into the form when the invite deep link points at #share.
    useEffect(() => {
        if (window.location.hash === "#share" && sectionRef.current) {
            setTimeout(() => sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
        }
    }, []);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.permission_to_publish) {
            toast.error("Please confirm we may publish your quote.");
            return;
        }
        if (form.quote.trim().length < 10) {
            toast.error("Your quote needs at least 10 characters.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/content/success-stories/submissions", { ...form, src: source || "direct", ref: params.get("ref") || "" });
            setSubmitted(true);
            toast.success("Thanks — we'll review it shortly.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <section className="mt-16" data-testid="share-story-success">
                <div className="card-soft p-8 text-center max-w-2xl mx-auto">
                    <CheckCircle2 size={28} className="mx-auto text-emerald-500" />
                    <h3 className="mt-3 font-heading font-semibold text-2xl text-foreground">Story received — thank you.</h3>
                    <p className="mt-2 text-sm text-muted-foreground">Our team reviews every submission for accuracy and reaches out before publishing anything with your name on it.</p>
                </div>
            </section>
        );
    }

    return (
        <section className="mt-16" id="share" ref={sectionRef} data-testid="share-story-section">
            <div className="max-w-3xl mx-auto card-soft p-7">
                <div className="flex items-center gap-2">
                    <Send size={14} className="text-emerald-500" />
                    <p className="overline mb-0">Share your story</p>
                </div>
                <h2 className="mt-2 font-heading font-medium text-3xl tracking-tight text-foreground">
                    Hired through Simonara? Tell us about it.
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                    A short quote is all we need. We&apos;ll reach out to confirm details before anything goes live.
                </p>
                {prefilled && source === "post_hire_invite" && (
                    <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-800 dark:text-emerald-300" data-testid="share-story-prefilled-banner">
                        <b>We&apos;ve pre-filled a few fields for you.</b> Just add a quick quote below — takes about 45 seconds.
                    </div>
                )}

                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="share-story-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="ss-company">Company name</label>
                            <input id="ss-company" data-testid="ss-company" type="text" required maxLength={180}
                                value={form.company_name} onChange={(e) => setField("company_name", e.target.value)}
                                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Acme Inc." />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="ss-role">Role you hired</label>
                            <input id="ss-role" data-testid="ss-role" type="text" required maxLength={180}
                                value={form.role_hired} onChange={(e) => setField("role_hired", e.target.value)}
                                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Senior Product Designer" />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="ss-quote">Your quote</label>
                        <textarea id="ss-quote" data-testid="ss-quote" required rows={4} minLength={10} maxLength={1200}
                            value={form.quote} onChange={(e) => setField("quote", e.target.value)}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm leading-relaxed"
                            placeholder="What did Simonara change for you? Two or three sentences is perfect." />
                        <div className="mt-1 text-[11px] text-muted-foreground text-right">{form.quote.length} / 1200</div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="ss-author">Your name</label>
                            <input id="ss-author" data-testid="ss-author" type="text" required maxLength={120}
                                value={form.author_name} onChange={(e) => setField("author_name", e.target.value)}
                                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Jane Doe" />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="ss-title">Your title <span className="text-muted-foreground font-normal">(optional)</span></label>
                            <input id="ss-title" data-testid="ss-title" type="text" maxLength={160}
                                value={form.author_title} onChange={(e) => setField("author_title", e.target.value)}
                                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="VP of People" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="ss-email">Your work email</label>
                            <input id="ss-email" data-testid="ss-email" type="email" required
                                value={form.author_email} onChange={(e) => setField("author_email", e.target.value)}
                                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="you@acme.com" />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="ss-tts">Time to shortlist <span className="text-muted-foreground font-normal">(optional)</span></label>
                            <input id="ss-tts" data-testid="ss-tts" type="text" maxLength={80}
                                value={form.time_to_shortlist} onChange={(e) => setField("time_to_shortlist", e.target.value)}
                                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="3 days" />
                        </div>
                    </div>

                    <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-border bg-muted/30 p-3" data-testid="ss-consent-wrap">
                        <input type="checkbox" required data-testid="ss-consent"
                            checked={form.permission_to_publish} onChange={(e) => setField("permission_to_publish", e.target.checked)} className="mt-1" />
                        <span className="text-xs text-muted-foreground leading-relaxed">
                            I confirm the quote above is my own and I permit Simonara Talent Solutions to publish it on marketing surfaces with my name and company attribution. I can request removal anytime by emailing <a href="mailto:privacy@simonaratalent.us" className="underline">privacy@simonaratalent.us</a>.
                        </span>
                    </label>

                    <div className="flex items-center gap-3">
                        <button type="submit" disabled={submitting} className="btn-green inline-flex items-center gap-2 disabled:opacity-60" data-testid="ss-submit">
                            {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : <>Share my story <ArrowRight size={14} /></>}
                        </button>
                        <span className="text-xs text-muted-foreground">Real humans review every submission.</span>
                    </div>
                </form>
            </div>
        </section>
    );
}
